import type { QuizConfig, QuizRound } from '../config/quiz.config';
import type { ShownQuestion } from './quizPool';

export const QuizPhase = {
  Idle: 'IDLE',
  /** The question is up, the clock and the bonus run down. */
  Asking: 'ASKING',
  /** Answered or timed out: the result stays for a moment. */
  Result: 'RESULT',
} as const;
export type QuizPhase = (typeof QuizPhase)[keyof typeof QuizPhase];

export interface QuizOutcome {
  readonly questionId: string;
  /** Index of the chosen answer (shown order), null when the time ran out. */
  readonly chosen: number | null;
  readonly correct: boolean;
  /** Bottles won (0 unless correct). */
  readonly bonus: number;
  /** Seconds it took to answer (the time limit when it ran out). */
  readonly seconds: number;
}

/**
 * One quiz round, engine- and DOM-free: the clock (real seconds, the game world is frozen meanwhile), the bonus
 * counting down with it, the answer and the result phase. `update` returns the outcome once, when the result
 * phase is over and the run may go on.
 */
export class QuizSession {
  private phaseNow: QuizPhase = QuizPhase.Idle;
  private shown: ShownQuestion | null = null;
  private elapsed = 0;
  private resultTime = 0;
  private result: QuizOutcome | null = null;
  /** Bonus bottles at the start of this round's clock. */
  private bonusStart = 0;

  constructor(private readonly config: QuizConfig) {}

  get phase(): QuizPhase {
    return this.phaseNow;
  }

  /** The quiz holds the run (asking or showing the result). */
  get active(): boolean {
    return this.phaseNow !== QuizPhase.Idle;
  }

  get question(): ShownQuestion | null {
    return this.shown;
  }

  get outcome(): QuizOutcome | null {
    return this.result;
  }

  /** Seconds left to answer (0 … time limit). */
  get secondsLeft(): number {
    return Math.max(0, this.config.timeLimitSeconds - this.elapsed);
  }

  /** Bonus bottles at the current moment: from the round's start bonus down to 0 over the time limit, in bonusStep steps. */
  get bonus(): number {
    return bonusAt(this.elapsed, this.bonusStart, this.config);
  }

  /** A new round: the question and its start bonus (QuizRound.bonus). */
  start(question: ShownQuestion, bonusStart: number): void {
    this.shown = question;
    this.bonusStart = bonusStart;
    this.phaseNow = QuizPhase.Asking;
    this.elapsed = 0;
    this.resultTime = 0;
    this.result = null;
  }

  /** The player picked an answer (shown order). Ignored outside the asking phase. */
  answer(index: number): void {
    if (this.phaseNow !== QuizPhase.Asking || !this.shown) return;
    if (index < 0 || index >= this.shown.answers.length) return;
    const correct = index === this.shown.correct;
    this.finish(index, correct, correct ? this.bonus : 0);
  }

  /** Per rendered frame (real seconds). Returns the outcome once, when the result phase ends. */
  update(dt: number): QuizOutcome | null {
    if (this.phaseNow === QuizPhase.Asking) {
      this.elapsed = Math.min(this.config.timeLimitSeconds, this.elapsed + dt);
      if (this.elapsed >= this.config.timeLimitSeconds) this.finish(null, false, 0);
      return null;
    }
    if (this.phaseNow === QuizPhase.Result) {
      this.resultTime += dt;
      if (this.resultTime >= this.config.resultSeconds) {
        this.phaseNow = QuizPhase.Idle;
        return this.result;
      }
    }
    return null;
  }

  reset(): void {
    this.phaseNow = QuizPhase.Idle;
    this.shown = null;
    this.elapsed = 0;
    this.resultTime = 0;
    this.result = null;
  }

  private finish(chosen: number | null, correct: boolean, bonus: number): void {
    if (!this.shown) return;
    this.result = { questionId: this.shown.id, chosen, correct, bonus, seconds: this.elapsed };
    this.phaseNow = QuizPhase.Result;
    this.resultTime = 0;
  }
}

/** Bonus bottles after `elapsed` seconds: linear from `start` to 0, rounded down to bonusStep. */
export function bonusAt(elapsed: number, start: number, config: Pick<QuizConfig, 'bonusStep' | 'timeLimitSeconds'>): number {
  const share = Math.max(0, 1 - elapsed / config.timeLimitSeconds);
  return Math.floor((start * share) / config.bonusStep) * config.bonusStep;
}

/** The round of a golden bottle at this run distance: the last one whose fromDistance it has reached. */
export function roundAt(distance: number, config: Pick<QuizConfig, 'rounds'>): QuizRound | null {
  let found: QuizRound | null = null;
  for (const round of config.rounds) if (round.fromDistance <= distance) found = round;
  return found ?? config.rounds[0] ?? null;
}
